import {
    JobHistoryReportsRequestParams,
    JobReportsResponse,
    ReportsAPI
} from '@vromo/api/reportsApi';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { useCallback, useState } from 'react';
import { RootStore } from '../../../stores/RootStore';
import { Filters } from '../../utils/reportTypes';
import { JobHistoryResponse } from './JobHistoryHelper';
import { downloadCSV as downloadCSVFromLink } from '../../helpers';

const MAX_PAGE_SIZE = 200;

interface Props {
    filters: Filters;
    groupId: number | null | undefined;
}

interface OutputProps {
    applyFiltersForJobHistory: (pageNum?: number) => Promise<void>;
    isLoading: boolean;
    pageNumber: number;
    totalPages: number;
    jobReports: JobHistoryResponse[];
    isLoadingCSV: boolean;
    downloadCSV: (csvTitle: string) => Promise<void>;
}

const useFetchJobHistory = ({ filters, groupId }: Props): OutputProps => {
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isLoadingCSV, setLoadingCSV] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [jobReports, setJobReports] = useState<JobHistoryResponse[]>([]);

    const {
        authStore: { user, token }
    } = useStore<RootStore>('rootStore');
    const { vehicleStore, viewStore } = useStore<RootStore>('rootStore');

    const buildRequestParams = (newPageNumber?: number) => {
        const {
            startDate,
            endDate,
            vehicles,
            teams,
            jobName,
            sites,
            policies,
            jobStatus,
            campaigns
        } = filters;
        const requestParams: JobHistoryReportsRequestParams = {
            ['group-id']: groupId,
            ['time-from']: startDate.getTime(),
            ['time-to']: endDate.getTime(),
            page: newPageNumber || 0,
            size: MAX_PAGE_SIZE
        };

        if (!!jobName) requestParams.name = jobName;
        if (!!jobStatus?.value) requestParams.status = (jobStatus.value as string).toUpperCase();
        requestParams.sites = sites.map((filterObject) => filterObject?.value as number);
        requestParams.policies = policies.map((filterObject) => filterObject?.value as number);

        requestParams.vehicles = vehicles.map((filterObject) => filterObject?.value as number);

        requestParams.groups = teams.map((filterObject) => filterObject?.value as number);
        requestParams.campaigns = campaigns.map((filterObject) => filterObject?.value as number);
        requestParams.isImperial = viewStore.imperialUnits || false
        return requestParams;
    };

    const applyFiltersForJobHistory = useCallback(
        async (newPageNumber?: number) => {
            try {
                if (!groupId) return;

                setLoading(true);
                setJobReports([]);
                setTotalPages(1);

                const api = new ReportsAPI(user.apiHost);

                const reportsResponse: JobReportsResponse | null = await api.getJobHistory.get(
                    buildRequestParams(newPageNumber),
                    token || ''
                );

                if (reportsResponse === null) return;

                const {
                    _embedded: { jobReports },
                    page
                } = reportsResponse as JobReportsResponse;

                setJobReports(jobReports);
                setTotalPages(page.totalPages);
                setPageNumber(page.number);
            } catch (e) {
                console.warn('Error: Failed to apply report filters: ', e);
            } finally {
                setLoading(false);
            }
        },
        [filters, groupId, token, user.apiHost, vehicleStore]
    );

    const downloadCSV = async (csvTitle: string) => {
        setLoadingCSV(true);
        if (!token) return;
        const api = new ReportsAPI(user.apiHost);
        const requestParams = {
            ...buildRequestParams(pageNumber),
            ['time-zone-id']: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        try {
            const csvResponse = await api.getJobHistoryReportsCSV.get(requestParams, token);
            if (!csvResponse) return;
            downloadCSVFromLink(csvResponse.reportDownloadLink as string, csvTitle);
            setLoadingCSV(false);
        } catch (e) {
            setLoadingCSV(false);
        }
    };

    return {
        applyFiltersForJobHistory,
        downloadCSV,
        isLoadingCSV,
        isLoading,
        pageNumber,
        totalPages,
        jobReports
    };
};

export default useFetchJobHistory;
